import { useTranslation } from "react-i18next";

import type { SupportedLanguage } from "@mountayaapp/locales";

import { listPageTabs } from "../../content/rules/elementTabs";
import { buildDocHref, normalizeHref } from "../../content/rules/href";
import classes from "../docsPageTabs.module.css";

/**
 * DocsPageTabsProps carry the active language (which the tab hrefs are built in) and the
 * slug the page is served at, which decides both the tab set and the one that is current.
 */
interface DocsPageTabsProps {
  lang: SupportedLanguage;
  slug: string;
}

/**
 * DocsPageTabs is the strip between a guide's breadcrumbs and its heading: Documentation,
 * then the tables that used to be interleaved with the prose.
 *
 * A generated props table is reference material, and reference material read in sequence
 * with prose interrupts both — `studio-react/chrome` carried 37 of them through 609 lines.
 * Splitting them the way `mantine.dev` does gives each one a page a reader can land on,
 * link to and search, without the guide having to carry it.
 *
 * The strip is DERIVED: `listPageTabs` computes it from the element manifests, so a page
 * gains its Props tab on the day an element is assigned to it. A page with nothing beyond
 * its own prose gets a one-entry list and this renders nothing at all, which is every
 * non-element page in the corpus — the strip is the exception, not the frame.
 *
 * The marks follow the header's rule one level down: `data-active` paints the underline
 * and `aria-current="page"` is the only one of the two a screen reader hears. The tabs are
 * full-reload anchors, since the docs app serves static pages with no client router.
 */
export const DocsPageTabs = ({ lang, slug }: DocsPageTabsProps) => {
  const { t } = useTranslation("docs");
  const tabs = listPageTabs(slug);

  if (tabs.length < 2) {
    return null;
  }

  const current = normalizeHref(buildDocHref(lang, slug));

  return (
    <nav aria-label={t("page.tabs")} className={classes.tabRow}>
      {tabs.map((tab) => {
        const href = buildDocHref(lang, tab.slug);
        const isCurrent = normalizeHref(href) === current;

        return (
          <a
            aria-current={isCurrent ? "page" : undefined}
            className={classes.tab}
            data-active={isCurrent ? true : undefined}
            href={href}
            key={tab.id}
          >
            {t(tab.labelKey)}
          </a>
        );
      })}
    </nav>
  );
};
